import React, { useEffect, useState, useCallback } from 'react';
import { FileText, RefreshCw, Search } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import UniversalLookup from './UniversalLookup';
import SmartPrintButton from './PrintButton';

const VendorStatement = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { fontSize } = useFontSize();

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [orgId, setOrgId] = useState(session.org_id || '');
  const [vendor, setVendor] = useState(null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState(new Date().toISOString().substring(0, 10));
  const [rows, setRows] = useState([]);
  const [closingBalance, setClosingBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!vendor) return;
    setLoading(true);
    try {
      const res = await api.get('/gl/vendor-statement', { params: { vendor: vendor.comp_num, org_id: orgId, from_date: fromDate || undefined, to_date: toDate } });
      setRows(res.data.data);
      setClosingBalance(res.data.closing_balance);
    } catch (err) {
      toast.error(t('common.load_error', 'Failed to load vendor statement'));
    } finally {
      setLoading(false);
    }
  }, [vendor, orgId, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup
        isOpen={lookupOpen}
        title={t('gl.vendor_statement.select_vendor', 'Select Vendor')}
        type="vendors"
        onClose={() => setLookupOpen(false)}
        onSelect={(row) => { setVendor(row); setLookupOpen(false); }}
      />

      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4 flex-wrap gap-3">
          <h2 className="text-primary-dark font-black italic text-lg flex items-center gap-2">
            <FileText size={22} className="text-status-warning" />
            {t('gl.vendor_statement.title', 'Vendor Statement')}
            <RefreshCw size={18} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`} onClick={fetchData} />
            {vendor && (
              <SmartPrintButton
                moduleName="VENDOR_STATEMENT"
                refId={vendor.comp_num}
                extraParams={{ vendor: vendor.comp_num, org_id: orgId, from_date: fromDate, to_date: toDate }}
              />
            )}
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.trial_balance.org', 'Organization')}</label>
              <input className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 uppercase" value={orgId} onChange={e => setOrgId(e.target.value.toUpperCase())} />
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.vendor_statement.vendor', 'Vendor')}</label>
              <div
                className="flex items-center gap-2 border-b-2 border-line-subtle py-1 cursor-pointer min-w-[160px]"
                onClick={() => setLookupOpen(true)}
              >
                <span className={`font-bold ${vendor ? 'text-primary' : 'text-ink-secondary italic font-normal'}`}>
                  {vendor ? `${vendor.comp_num} - ${vendor.name}` : t('gl.vendor_statement.pick_vendor', 'Select...')}
                </span>
                <Search size={14} className="text-primary shrink-0" />
              </div>
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.from', 'From')}</label>
              <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.to', 'To')}</label>
              <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.date', 'Date')}</th>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.entry_num', 'Entry #')}</th>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.source', 'Source')}</th>
                <th className="p-3 border-x border-white/10">{t('gl.vendor_statement.fields.ref', 'Reference')}</th>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.description', 'Description')}</th>
                <th className="p-3 border-x border-white/10 text-center w-32">{t('gl.trial_balance.fields.debit', 'Debit')}</th>
                <th className="p-3 border-x border-white/10 text-center w-32">{t('gl.trial_balance.fields.credit', 'Credit')}</th>
                <th className="p-3 border-x border-white/10 text-center w-32">{t('gl.vendor_statement.fields.balance_owed', 'Balance Owed')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle font-bold">
              {!vendor ? (
                <tr><td colSpan="8" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('gl.vendor_statement.pick_vendor_hint', 'Select a vendor to view their statement')}</td></tr>
              ) : loading ? (
                <tr><td colSpan="8" className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="8" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('gl.vendor_statement.no_records', 'No transactions for this vendor')}</td></tr>
              ) : (
                rows.map(r => (
                  <tr key={r.entry_num} className="hover:bg-primary-soft">
                    <td className="p-3 border-x border-line-subtle text-ink-secondary">{r.entry_date ? new Date(r.entry_date).toLocaleDateString() : ''}</td>
                    <td className="p-3 border-x border-line-subtle text-primary">{r.entry_num}</td>
                    <td className="p-3 border-x border-line-subtle text-center">
                      <span className="bg-status-warning-bg text-status-warning px-2 py-0.5 rounded-full text-[9px] shadow-sm border border-status-warning-bg">
                        {r.source}
                      </span>
                    </td>
                    <td className="p-3 border-x border-line-subtle text-ink-secondary normal-case">{r.source_ref}</td>
                    <td className="p-3 border-x border-line-subtle text-ink normal-case italic">{r.description}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-success">{r.debit ? Number(r.debit).toLocaleString() : ''}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-danger">{r.credit ? Number(r.credit).toLocaleString() : ''}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-primary">{Number(r.balance).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
            {vendor && rows.length > 0 && (
              <tfoot>
                <tr className="bg-surface-panel font-black">
                  <td colSpan="7" className="p-3 border-x border-line-subtle text-right">{t('gl.vendor_statement.total_owed', 'Total Owed')}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-primary">{Number(closingBalance).toLocaleString()}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default VendorStatement;
